import React from "react";
import PropTypes from "prop-types";
import ImageGenerationSteps from "./ImageGenerationSteps";

/**
 * Loading placeholder for image elements that shows generation progress
 */
export default function ImageLoadingPlaceholder({ 
  message, 
  progress,
  currentStatus = undefined,
  size = { width: 500, height: 667 },
  position = { x: 0, y: 0 },
  // Optional customization for more specific contexts (kept backwards-compatible)
  customTitle = undefined,
  customDescription = undefined,
  showSteps = true,
}) {
  // Calculate progress percentage for the bar
  const progressPercentage = Math.min(100, Math.max(0, progress || 0));
  
  // Use actual status if provided, otherwise fall back to progress-based derivation
  const currentStep = currentStatus || (() => {
    const p = progress || 0;
    if (p >= 76) return "updatingImageElements";
    if (p >= 56) return "generatingCoverImage";
    if (p >= 31) return "generatingImagePrompts";
    return "generatingCharacter";
  })();
  
  // Log when placeholder renders
  console.log('[SBW:ImageLoadingPlaceholder] Rendering:', {
    message,
    progress,
    progressPercentage,
    currentStatus,
    currentStep,
    size,
    position,
    timestamp: new Date().toISOString()
  });
  
  return (
    <div 
      className="absolute flex flex-col items-center justify-center bg-white border-2 border-dashed border-indigo-300 rounded-lg overflow-hidden shadow-lg"
      style={{
        width: `${size.width}px`,
        height: `${size.height}px`,
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 10,
        pointerEvents: 'none', // So it doesn't interfere with interactions
      }}
    >
      <div className="text-center p-6 w-full">
        {/* Icon that pulses */}
        <div className="animate-pulse flex justify-center mb-4">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-16 w-16 text-indigo-400" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={1.5} 
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
            />
          </svg>
        </div>

        {/* Detailed generation steps (can be hidden for custom prompt UI) */}
        {showSteps && (
          <ImageGenerationSteps currentStep={currentStep} progress={progressPercentage} />
        )}
        
        {/* Optional custom copy for more specific flows */}
        {(customTitle || customDescription) && (
          <div className="mt-3">
            {customTitle && (
              <p className="text-sm font-semibold text-indigo-700">{customTitle}</p>
            )}
            {customDescription && (
              <p className="text-xs text-gray-600">{customDescription}</p>
            )}
          </div>
        )}
        
        {/* Note about editing */}
        <div className="mt-5 px-2">
          <p className="text-xs text-gray-500">
            You can continue editing while images are being generated
          </p>
        </div>
      </div>
    </div>
  );
}

ImageLoadingPlaceholder.propTypes = {
  message: PropTypes.string,
  progress: PropTypes.number,
  currentStatus: PropTypes.string,
  size: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number
  }),
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  customTitle: PropTypes.string,
  customDescription: PropTypes.string,
  showSteps: PropTypes.bool,
};
